/**
 * Client-side mirror of functions/src/lib/conditions.js.
 *
 * Kept as a small deliberate duplicate rather than a shared package: the functions
 * bundle is CommonJS and lives outside the Vite root, and this file is ~40 lines. If a
 * third consumer ever appears, promote both to a shared workspace package.
 *
 * Used for two things in the UI:
 *   - conditional field reveal in the intake form (`visibleIf`)
 *   - previewing which approval steps a matter would trigger, in the admin panel
 */

export function getPath(obj, path) {
  if (!path) return undefined;
  return String(path)
    .split('.')
    .reduce((acc, key) => (acc === null || acc === undefined ? undefined : acc[key]), obj);
}

const asArray = (v) => (Array.isArray(v) ? v : v === undefined || v === null ? [] : [v]);

const OPERATORS = {
  eq: (a, e) => a === e,
  neq: (a, e) => a !== e,
  in: (a, e) => asArray(e).includes(a),
  nin: (a, e) => !asArray(e).includes(a),
  contains: (a, e) => asArray(a).includes(e),
  contains_any: (a, e) => asArray(a).some((x) => asArray(e).includes(x)),
  truthy: (a) => Boolean(a),
  falsy: (a) => !a,
  is_false: (a) => a === false,
  is_true: (a) => a === true,
  exists: (a) => a !== undefined && a !== null && a !== '',
  not_exists: (a) => a === undefined || a === null || a === '',
  gt: (a, e) => Number(a) > Number(e),
  gte: (a, e) => Number(a) >= Number(e),
  lt: (a, e) => Number(a) < Number(e),
  lte: (a, e) => Number(a) <= Number(e),
  non_empty: (a) => asArray(a).length > 0,
};

export function evaluateCondition(condition, data) {
  if (condition === undefined || condition === null || condition === true) return true;
  if (condition === false) return false;
  if (Array.isArray(condition.all)) return condition.all.every((c) => evaluateCondition(c, data));
  if (Array.isArray(condition.any)) return condition.any.some((c) => evaluateCondition(c, data));
  if (condition.not !== undefined) return !evaluateCondition(condition.not, data);
  if (condition.op === 'some') {
    const rows = getPath(data, condition.path);
    return Array.isArray(rows) && rows.some((row) => evaluateCondition(condition.value, row));
  }

  const op = OPERATORS[condition.op];
  if (!op) {
    // Configurable conditions must never fail open. Keep the form safe and make the
    // misconfiguration visible to the administrator using the browser console.
    // eslint-disable-next-line no-console
    console.warn(`Unknown condition operator: ${String(condition.op)}`, condition);
    return false;
  }
  return Boolean(op(getPath(data, condition.path), condition.value));
}

/** Immutable dotted-path write, used by every form field's onChange. */
export function setPath(obj, path, value) {
  const keys = String(path).split('.');
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  let cursor = clone;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const key = keys[i];
    const existing = cursor[key];
    cursor[key] =
      existing === null || typeof existing !== 'object'
        ? {}
        : Array.isArray(existing)
          ? [...existing]
          : { ...existing };
    cursor = cursor[key];
  }
  cursor[keys[keys.length - 1]] = value;
  return clone;
}

/** Immutable dotted-path delete. Missing paths are left untouched. */
export function unsetPath(obj, path) {
  const keys = String(path).split('.');
  const clone = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  let source = obj;
  let cursor = clone;

  for (let i = 0; i < keys.length - 1; i += 1) {
    const key = keys[i];
    const existing = source?.[key];
    if (existing === null || typeof existing !== 'object') return clone;
    cursor[key] = Array.isArray(existing) ? [...existing] : { ...existing };
    source = existing;
    cursor = cursor[key];
  }

  delete cursor[keys[keys.length - 1]];
  return clone;
}
